import {
    Box, Grid, Typography, TextField, Button, InputAdornment, Divider
} from '@mui/material'
import React, { useCallback, useEffect, useState } from 'react'
import EditIcon from '@mui/icons-material/Edit'
import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import SwapHorizIcon from '@mui/icons-material/SwapHoriz'
import FunctionsIcon from '@mui/icons-material/Functions'
import PopupDialog from '../../Components/Popups/ConfirmDialog'
import { ApiGetDepositFees, ApiUpdateDepositFees } from '../../apiService/Core/Apicall'
import { useNavigate } from 'react-router-dom'
import PlatformFeesSelection from './PlatformFeesSelection'
import DateRangeFilter from '../../Components/Common/DateRangeFilter'
import { useDateRange } from '../../hooks/useDateRange'

interface FeesData {
    _id: string
    deposit_fees: number
    platform_trade_fees: number
    total_deposit_platform_fees: number
    total_trade_fees: number
    createdAt: string
    updatedAt: string
    __v: number
}

const StatCard: React.FC<{
    label: string
    value: string
    sub?: string
    color: string
    icon: React.ReactNode
}> = ({ label, value, sub, color, icon }) => (
    <Box sx={{
        background: 'linear-gradient(135deg, #2a2a2a 0%, #222 100%)',
        border: '1px solid #333',
        borderRadius: '14px',
        p: 2.5,
        position: 'relative',
        overflow: 'hidden',
        transition: 'border-color 0.25s',
        '&:hover': { borderColor: color + '60' },
        '&::before': {
            content: '""',
            position: 'absolute',
            top: 0, left: 0, right: 0,
            height: '2px',
            background: `linear-gradient(90deg, ${color}00, ${color}, ${color}00)`,
        },
    }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
            <Typography fontSize={10} color="#6f6f6f" textTransform="uppercase" letterSpacing="0.1em">
                {label}
            </Typography>
            <Box sx={{ color, opacity: 0.7 }}>{icon}</Box>
        </Box>
        <Typography fontSize={20} fontWeight={800} color={color} sx={{ letterSpacing: '-0.02em' }}>
            {value}
        </Typography>
        {sub && <Typography fontSize={10} color="#5a5a5a" mt={0.5}>{sub}</Typography>}
    </Box>
)

const FeesManagement: React.FC = () => {
    const [fees, setFees] = useState<FeesData | null>(null)
    const [editing, setEditing] = useState(false)
    const [inputValue, setInputValue] = useState('')
    const [inputError, setInputError] = useState('')
    const [platformInputValue, setPlatformInputValue] = useState('')
    const [platformInputError, setPlatformInputError] = useState('')
    const [updateLoading, setUpdateLoading] = useState(false)
    const [confirmOpen, setConfirmOpen] = useState(false)
    const navigate = useNavigate()

    const {
        range, customStart, setCustomStart, customEnd, setCustomEnd, handleRangeChange, buildQuery,
    } = useDateRange('deposit-fees', 'all')

    const [errorPopup, setErrorPopup] = useState<{ open: boolean; message: string }>({
        open: false, message: ''
    })
    const [successPopup, setSuccessPopup] = useState<{ open: boolean; message: string }>({
        open: false, message: ''
    })

    const fetchFees = useCallback(async () => {
        try {
            const query = buildQuery()
            if (query === null) return
            const res = await ApiGetDepositFees(`?${query}`)
            if (res?.status) {
                setFees(res.data)
                setInputValue(String(res.data.deposit_fees))
                setPlatformInputValue(String(res.data.platform_trade_fees))
            }
        } catch (error: any) {
            setErrorPopup({
                open: true,
                message: error?.message || error?.response?.data?.message || 'Failed to fetch fees',
            })
        }
    }, [buildQuery])

    useEffect(() => {
        fetchFees()
    }, [fetchFees])

    const handleEdit = () => {
        setInputValue(String(fees?.deposit_fees ?? ''))
        setPlatformInputValue(String(fees?.platform_trade_fees ?? ''))
        setInputError('')
        setPlatformInputError('')
        setEditing(true)
    }

    const handleCancel = () => {
        setEditing(false)
        setInputError('')
        setPlatformInputError('')
        setInputValue(String(fees?.deposit_fees ?? ''))
        setPlatformInputValue(String(fees?.platform_trade_fees ?? ''))
    }

    const handleConfirmClick = () => {
        const val = Number(inputValue)
        const pVal = Number(platformInputValue)
        let hasError = false
        if (!inputValue || isNaN(val) || val < 0) {
            setInputError('Enter a valid fee value (≥ 0)')
            hasError = true
        } else if (val > 100) {
            setInputError('Fee cannot exceed 100%')
            hasError = true
        } else {
            setInputError('')
        }

        if (!platformInputValue || isNaN(pVal) || pVal < 0) {
            setPlatformInputError('Enter a valid fee value (≥ 0)')
            hasError = true
        } else if (pVal > 100) {
            setPlatformInputError('Fee cannot exceed 100%')
            hasError = true
        } else {
            setPlatformInputError('')
        }

        if (!hasError) setConfirmOpen(true)
    }

    const handleUpdate = async () => {
        setUpdateLoading(true)
        try {
            const payload: Record<string, number> = {}
            if (Number(inputValue) !== fees?.deposit_fees) {
                payload.deposit_fees = Number(inputValue)
            }
            if (Number(platformInputValue) !== fees?.platform_trade_fees) {
                payload.platform_trade_fees = Number(platformInputValue)
            }
            const res = await ApiUpdateDepositFees(payload)
            if (res?.status) {
                setSuccessPopup({
                    open: true,
                    message: res?.message || 'Fees updated successfully',
                })
                setEditing(false)
                fetchFees()
            }
        } catch (error: any) {
            setErrorPopup({
                open: true,
                message: error?.message || error?.response?.data?.message || 'Failed to update fees',
            })
        } finally {
            setUpdateLoading(false)
        }
    }

    const QUICK_FEES = [0.5, 1, 1.5, 2, 2.5, 5]

    const confirmDetails = () => {
        const details: { label: string; value: string; mono?: boolean }[] = []
        if (Number(inputValue) !== fees?.deposit_fees) {
            details.push({ label: 'Previous Deposit Fee', value: `${fees?.deposit_fees ?? 0}%` })
            details.push({ label: 'New Deposit Fee', value: `${inputValue}%` })
        }
        if (Number(platformInputValue) !== fees?.platform_trade_fees) {
            details.push({ label: 'Previous Platform Trade Fee', value: `${fees?.platform_trade_fees ?? 0}%` })
            details.push({ label: 'New Platform Trade Fee', value: `${platformInputValue}%` })
        }
        details.push({
            label: 'Timestamp',
            value: new Date().toLocaleString('en-US', {
                month: 'short', day: 'numeric', year: 'numeric',
                hour: '2-digit', minute: '2-digit'
            }),
            mono: true,
        })
        return details
    }

    return (
        <>
            <DateRangeFilter
                range={range}
                onRangeChange={handleRangeChange}
                customStart={customStart}
                onCustomStartChange={setCustomStart}
                customEnd={customEnd}
                onCustomEndChange={setCustomEnd}
            />
            <Grid container spacing={2} mb={2} mt={2}>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <StatCard
                        label="Total Deposit Platform Fees"
                        value={!fees ? '—' : `$${fees.total_deposit_platform_fees}`}
                        sub="Cumulative deposit platform fee collected"
                        color="#f2674a"
                        icon={<ArrowDownwardIcon fontSize="small" />}
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <Box onClick={() => navigate('/admin/trade-fees')} sx={{ cursor: 'pointer' }}>
                        <StatCard
                            label="Total Trade Fees"
                            value={!fees ? '—' : `$${fees.total_trade_fees.toFixed(2)}`}
                            sub="Cumulative platform trade fee collected"
                            color="#a78bfa"
                            icon={<SwapHorizIcon fontSize="small" />}
                        />
                    </Box>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <StatCard
                        label="Combined Total Fees"
                        value={!fees ? '—' : `$${(fees.total_deposit_platform_fees + fees.total_trade_fees).toFixed(2)}`}
                        sub="Total deposit + trade fees combined"
                        color="#3b82f6"
                        icon={<FunctionsIcon fontSize="small" />}
                    />
                </Grid>
            </Grid>

            <Box className="bg-dark-color border-10 p-4">
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                    <Box>
                        <Typography fontSize={15} fontWeight={700} color="#e0e0e0">
                            Fee Configuration
                        </Typography>
                        <Typography fontSize={12} color="#5a5a5a" mt={0.4}>
                            This fee is applied as a percentage on all transactions.
                        </Typography>
                    </Box>
                    {!editing && (
                        <Button
                            variant="contained"
                            startIcon={<EditIcon />}
                            onClick={handleEdit}
                            sx={{
                                background: 'linear-gradient(135deg, #f2674a, #d94f35)',
                                borderRadius: '10px', fontWeight: 700, px: 3, py: 1,
                                boxShadow: '0 4px 16px rgba(242,103,74,.35)',
                                '&:hover': { background: 'linear-gradient(135deg, #e05a3f, #c44530)' },
                            }}
                        >
                            Edit Fee
                        </Button>
                    )}
                </Box>

                <Divider sx={{ borderColor: '#2a2a2a', mb: 3 }} />

                {!fees ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
                        <Typography color="#5a5a5a">No fee data available</Typography>
                    </Box>
                ) : (
                    <Box sx={{ maxWidth: 560 }}>
                        {!editing && (
                            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                <Box sx={{
                                    flex: 1, minWidth: 200,
                                    background: '#1e1e1e', border: '1px solid #2f2f2f',
                                    borderRadius: '14px', p: 3,
                                    display: 'flex', alignItems: 'center', gap: 2,
                                }}>
                                    <Box sx={{
                                        width: 60, height: 60, borderRadius: '14px',
                                        background: 'linear-gradient(135deg, #f2674a20, #f2674a08)',
                                        border: '1px solid #f2674a30',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                                    }}>
                                        <Typography fontSize={20} fontWeight={900} color="#f2674a">{fees?.deposit_fees}</Typography>
                                    </Box>
                                    <Box>
                                        <Typography fontSize={10} color="#5a5a5a" letterSpacing="0.08em" textTransform="uppercase" mb={0.5}>
                                            Deposit Fee
                                        </Typography>
                                        <Typography fontSize={26} fontWeight={800} color="#f2674a" sx={{ letterSpacing: '-0.02em', lineHeight: 1 }}>
                                            {fees?.deposit_fees}%
                                        </Typography>
                                    </Box>
                                </Box>

                                <Box sx={{
                                    flex: 1, minWidth: 200,
                                    background: '#1e1e1e', border: '1px solid #2f2f2f',
                                    borderRadius: '14px', p: 3,
                                    display: 'flex', alignItems: 'center', gap: 2,
                                }}>
                                    <Box sx={{
                                        width: 60, height: 60, borderRadius: '14px',
                                        background: 'linear-gradient(135deg, #a78bfa20, #a78bfa08)',
                                        border: '1px solid #a78bfa30',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                                    }}>
                                        <Typography fontSize={20} fontWeight={900} color="#a78bfa">{fees?.platform_trade_fees}</Typography>
                                    </Box>
                                    <Box>
                                        <Typography fontSize={10} color="#5a5a5a" letterSpacing="0.08em" textTransform="uppercase" mb={0.5}>
                                            Default Platform Trade Fee
                                        </Typography>
                                        <Typography fontSize={26} fontWeight={800} color="#a78bfa" sx={{ letterSpacing: '-0.02em', lineHeight: 1 }}>
                                            {fees?.platform_trade_fees}%
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>
                        )}

                        {editing && (
                            <Box>
                                <Typography fontSize={11} color="#5a5a5a" mb={1} letterSpacing="0.08em" textTransform="uppercase">
                                    New Deposit Fee (%)
                                </Typography>
                                <TextField
                                    fullWidth
                                    placeholder="0.00"
                                    type="number"
                                    value={inputValue}
                                    onChange={(e) => { setInputValue(e.target.value); setInputError('') }}
                                    error={!!inputError}
                                    helperText={inputError}
                                    sx={inputStyle('#f2674a')}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <Typography sx={{ color: '#f2674a', fontWeight: 800, fontSize: 22, lineHeight: 1 }}>%</Typography>
                                            </InputAdornment>
                                        ),
                                        sx: { fontSize: 22, fontWeight: 700, color: '#fff' }
                                    }}
                                    inputProps={{ min: 0, max: 100, step: 0.1 }}
                                />
                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 2, mb: 3 }}>
                                    {QUICK_FEES.map((fee) => (
                                        <Box
                                            key={fee}
                                            onClick={() => { setInputValue(String(fee)); setInputError('') }}
                                            sx={{
                                                px: 1.8, py: 0.7, borderRadius: '7px', cursor: 'pointer',
                                                fontSize: 12, fontWeight: 600, border: '1px solid #333',
                                                color: inputValue === String(fee) ? '#f2674a' : '#6f6f6f',
                                                background: inputValue === String(fee) ? '#f2674a15' : '#1e1e1e',
                                                borderColor: inputValue === String(fee) ? '#f2674a50' : '#333',
                                                transition: 'all 0.15s',
                                                '&:hover': { borderColor: '#f2674a50', color: '#f2674a' }
                                            }}
                                        >
                                            {fee}%
                                        </Box>
                                    ))}
                                </Box>

                                <Typography fontSize={11} color="#5a5a5a" mb={1} letterSpacing="0.08em" textTransform="uppercase">
                                    New Platform Trade Fee (%)
                                </Typography>
                                <TextField
                                    fullWidth
                                    placeholder="0.00"
                                    type="number"
                                    value={platformInputValue}
                                    onChange={(e) => { setPlatformInputValue(e.target.value); setPlatformInputError('') }}
                                    error={!!platformInputError}
                                    helperText={platformInputError}
                                    sx={inputStyle('#a78bfa')}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <Typography sx={{ color: '#a78bfa', fontWeight: 800, fontSize: 22, lineHeight: 1 }}>%</Typography>
                                            </InputAdornment>
                                        ),
                                        sx: { fontSize: 22, fontWeight: 700, color: '#fff' }
                                    }}
                                    inputProps={{ min: 0, max: 100, step: 0.1 }}
                                />
                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 2, mb: 3 }}>
                                    {QUICK_FEES.map((fee) => (
                                        <Box
                                            key={fee}
                                            onClick={() => { setPlatformInputValue(String(fee)); setPlatformInputError('') }}
                                            sx={{
                                                px: 1.8, py: 0.7, borderRadius: '7px', cursor: 'pointer',
                                                fontSize: 12, fontWeight: 600, border: '1px solid #333',
                                                color: platformInputValue === String(fee) ? '#a78bfa' : '#6f6f6f',
                                                background: platformInputValue === String(fee) ? '#a78bfa15' : '#1e1e1e',
                                                borderColor: platformInputValue === String(fee) ? '#a78bfa50' : '#333',
                                                transition: 'all 0.15s',
                                                '&:hover': { borderColor: '#a78bfa50', color: '#a78bfa' }
                                            }}
                                        >
                                            {fee}%
                                        </Box>
                                    ))}
                                </Box>

                                <Box sx={{
                                    background: '#f59e0b0d',
                                    border: '1px solid #f59e0b25',
                                    borderRadius: '10px',
                                    p: 2, mb: 3,
                                    display: 'flex', gap: 1.5, alignItems: 'flex-start'
                                }}>
                                    <Box sx={{ color: '#f59e0b', mt: 0.2, fontSize: 16 }}>⚠</Box>
                                    <Box>
                                        <Typography fontSize={12} color="#f59e0b" fontWeight={600} mb={0.3}>
                                            Fee Change Impact
                                        </Typography>
                                        <Typography fontSize={11} color="#7a6a4a">
                                            Updating the fee will apply to all new deposit transactions. Existing transactions will not be affected.
                                        </Typography>
                                    </Box>
                                </Box>

                                <Box sx={{ display: 'flex', gap: 1.5 }}>
                                    <Button
                                        fullWidth
                                        variant="outlined"
                                        startIcon={<CloseIcon />}
                                        onClick={handleCancel}
                                        sx={{
                                            borderColor: '#333', color: '#7a7a7a', borderRadius: '10px',
                                            '&:hover': { borderColor: '#555', color: '#fff' }
                                        }}
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        fullWidth
                                        variant="contained"
                                        startIcon={updateLoading ? undefined : <CheckIcon />}
                                        disabled={updateLoading}
                                        onClick={handleConfirmClick}
                                        sx={{
                                            background: 'linear-gradient(135deg, #f2674a, #d94f35)',
                                            borderRadius: '10px', fontWeight: 700,
                                            boxShadow: '0 4px 16px rgba(242,103,74,.4)',
                                            '&:hover': { background: 'linear-gradient(135deg, #e05a3f, #c44530)' },
                                        }}
                                    >
                                        {updateLoading
                                            ? 'Updating...'
                                            : 'Update Fee'
                                        }
                                    </Button>
                                </Box>
                            </Box>
                        )}
                    </Box>
                )}
            </Box>

            <PlatformFeesSelection />

            <PopupDialog
                open={confirmOpen}
                onClose={() => setConfirmOpen(false)}
                onConfirm={() => { setConfirmOpen(false); handleUpdate() }}
                loading={updateLoading}
                variant="deposit"
                title="Confirm Fee Update"
                message="You are about to update the fee rate(s). This will affect all future transactions."
                confirmText="Confirm Update"
                details={confirmDetails()}
                notice="This change will apply immediately to all new deposit transactions going forward."
            />

            <PopupDialog
                open={errorPopup.open}
                onClose={() => setErrorPopup({ open: false, message: '' })}
                onConfirm={() => setErrorPopup({ open: false, message: '' })}
                variant="error"
                title="Something Went Wrong"
                message={errorPopup.message}
                confirmText="Try Again"
                cancelText="Dismiss"
            />

            <PopupDialog
                open={successPopup.open}
                onClose={() => setSuccessPopup({ open: false, message: '' })}
                onConfirm={() => setSuccessPopup({ open: false, message: '' })}
                variant="success"
                title="Success"
                message={successPopup.message}
                confirmText="Done"
                cancelText="Close"
            />
        </>
    )
}

export default FeesManagement

const inputStyle = (accentColor: string) => ({
    '& input': { color: '#fff', fontSize: 22, fontWeight: 700, py: 1.5 },
    '& .MuiOutlinedInput-root': {
        borderRadius: '10px',
        '& fieldset': { borderColor: '#333' },
        '&:hover fieldset': { borderColor: accentColor + '50' },
        '&.Mui-focused fieldset': { borderColor: accentColor },
    },
    '& .MuiFormHelperText-root': { color: '#ef4444', fontSize: 11 },
})